import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  Dialog,
  Icon,
  Icons,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Popover
} from '@clientbase/clientbase-library'
import {
  Box,
  Chip as MuiChip,
  Grid,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material'
import {
  resyncBillingRecurrenceContaAzul,
  syncBillingContaAzul
} from 'api/contaAzul'
import { DataService } from 'api/DataService'
import { useAuthContext } from 'context/AuthContext'
import copy from 'copy-to-clipboard'
import { useRemoveRecurrenceCoupon } from 'features/coupons/api/useRemoveRecurrenceCoupon'
import CouponValueDisplay from 'features/coupons/components/CouponValueDisplay'
import { usePermission } from 'hooks/usePermission'
import { useActiveMerchantForRecord } from 'lib/merchant-group'
import { BillingsParams } from 'models/Billings'
import historicChangesMapper, {
  Historic
} from 'models/Historic/HistoricChanges'
import {
  amountTypeEnum,
  issueWhenEnum,
  natureOperationEnum
} from 'models/Invoice'
import { constructorRecurrences, RecurrencesParams } from 'models/Recurrences'
import moment from 'moment'
import { ChargeRecurrenceBodyModalForm } from 'templates/Charge/interface'
import { mapRecurrenceToDuplicateFormValues } from 'templates/Customers/Modals/BodyModalNewChargeOrRecurrence/BodyModalNewChargeOrRecurrence.edit.utils'
import { formatPhone } from 'templates/Customers/Table/utils'

import { ButtonRounded } from 'components/button'
import Can from 'components/Can'
import CanLogical from 'components/CanLogical'
import ContaAzulRecurrenceTable, {
  ContaAzulResyncFooter
} from 'components/ContaAzulRecurrenceAccordion'
import { DetailCompanyCell } from 'components/DetailCompany'
import DetailSummary, {
  Celula,
  DescricaoRecolhivel,
  ListaChips,
  NaoConfigurado,
  PagadorFaixa
} from 'components/DetailSummary'
import { REQUIRES_REREGISTRATION_TOOLTIP } from 'components/DrawerCreditCard/PreviewCards/PreviewCards.mapper'
import IconContainer from 'components/IconContainer/IconContainer'
import ApplyAdjustmentDialog from 'components/Modal/ApplyAdjustmentDialog'
import ModalChargeDetail from 'components/Modal/ModalChargeDetail'
import CancelConfirmationModal from 'components/Modal/ModalChargeDetail/components/CancelConfirmationDialog'
import ModalContaAzulDetail from 'components/Modal/ModalContaAzulDetail'
import ModalEditRecurrenceUnified from 'components/Modal/ModalEditRecurrenceUnified'
import {
  headerGridMobileSx,
  mobile,
  modalHeaderMobileSx
} from 'components/Modal/modalHeaderMobile.styles'
import ModalInstallmentDetail from 'components/Modal/ModalInstallmentDetail'
import ModalNewRecurrence from 'components/Modal/ModalNewRecurrence'
import ModalReactivateRecurrence from 'components/Modal/ModalReactivateRecurrence'
import { Table } from 'components/Table'
import { FONT_SIZE_PX } from 'components/Table/tableDisplay'
import { ChipTagData } from 'components/Tag'
import ChipTag from 'components/Tag'
import { Tag } from 'components/TagsAddInput/TagsAddInput'
import TagsPopover from 'components/TagsPopover'

import { formatCpfCnpj, formatMoney } from 'utils'
import { formatMetadataValue } from 'utils/format-metadata-value'
import { describeFrequency } from 'utils/frequency'
import { convertTagsStringToArray } from 'utils/tags'

import { theme } from 'styles/theme'

import { Wrapper } from '../../../templates/Customers/Drawers/GroupsDrawer/components/NewGroupForm/styles'
import {
  compositionDetailMapper,
  getItemBonusValue
} from '../ModalChargeDetail/components/Composition/Composition'
import NegativateDetail from '../ModalChargeDetail/components/NegativateDetail'
import BonusCarryoverDetail from './components/BonusCarryoverDetail'
import CarneDialog from './components/CarneDialog'
import CreditCardDetailsDialog from './components/CreditCardDetailsDialog'
import PaymentTypeDetailsView from './components/PaymentTypeDetailsView'
import {
  CycleBilling,
  resolveHighlightedCycles
} from './ModalRecurrenceDetail.cycles'
import {
  getAdjustmentTypeLabel,
  getChargeEnum,
  getFrequency,
  getHoverText,
  getStatus,
  getStatusOptionCharge,
  getStatusStyleCharge,
  reasonTypeMapper
} from './ModalRecurrenceDetail.mappers'
import * as S from './ModalRecurrenceDetail.styles'

type ModalRecurrenceDetailProps = {
  uuid: string
  reloadTable?: React.Dispatch<React.SetStateAction<boolean>>
  handleCloseDetailRecurrence: (value: boolean) => void
  isOpenModalRecurrenceDetail: boolean
  onOpenDetailForUuid?: (uuid: string) => void
}

interface TagForm {
  tags: Tag[]
}

// Base do chip de status dos ciclos, igual ao que já era usado na tabela
const cycleStatusChipSx = {
  width: 'fit-content',
  padding: '4px 8px',
  fontWeight: 600,
  borderRadius: 9999
}

const ModalRecurrenceDetail = ({
  uuid,
  reloadTable,
  handleCloseDetailRecurrence,
  isOpenModalRecurrenceDetail,
  onOpenDetailForUuid
}: ModalRecurrenceDetailProps) => {
  // SC-10999: aberto da lista agregada do grupo, age na empresa do registro.
  useActiveMerchantForRecord(isOpenModalRecurrenceDetail ? uuid : undefined)
  const [loadingModal, setLoadingModal] = useState<boolean>(true)
  const [reload, setReload] = useState(false)
  const [syncingBillingUuid, setSyncingBillingUuid] = useState<string | null>(
    null
  )
  const [resyncingRecurrence, setResyncingRecurrence] = useState<boolean>(false)
  // Reenviar cobranca para a Conta Azul e acao de cobranca, nao de integracao:
  // o backend exige billings:manage (conta_azul/billings#sync e #retry_recurrence).
  const canManageContaAzulBillings = usePermission('billings:manage')

  const handleSyncBilling = async (billingUuid?: string) => {
    if (!billingUuid) return
    try {
      setSyncingBillingUuid(billingUuid)
      await syncBillingContaAzul(billingUuid)
      toast.success('Sincronização com a Conta Azul enfileirada.')
      setReload((prev) => !prev)
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao sincronizar com a Conta Azul.')
    } finally {
      setSyncingBillingUuid(null)
    }
  }

  // Reenvia todas as billings da recorrência de uma vez (notificação in-app consolidada).
  const handleResyncBillingRecurrence = async () => {
    const recurrenceUuid = recurrence?.uuid
    if (!recurrenceUuid) return
    try {
      setResyncingRecurrence(true)
      await resyncBillingRecurrenceContaAzul(recurrenceUuid)
      toast.success(
        'Sincronização da recorrência com a Conta Azul enfileirada.'
      )
      setReload((prev) => !prev)
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao sincronizar com a Conta Azul.')
    } finally {
      setResyncingRecurrence(false)
    }
  }
  const [confirmCancelScheduled, setConfirmCancelScheduled] = useState(false)
  const [recurrence, setRecurrence] = useState<RecurrencesParams>(
    {} as RecurrencesParams
  )
  const [paymentType, setPaymentType] = useState<any[]>([])

  // Linhas do detalhe CA da recorrência — compartilhadas pela tabela e pelo footer
  // "Sincronizar novamente".
  // Os mesmos meios que a grade renderiza: o backend devolve `boleto_pix`
  // junto do pix, e o modal não exibe esse par.
  const meiosDePagamentoVisiveis = paymentType.filter(
    (item) => item?.value && item.value !== 'boleto_pix'
  )

  // Mesma regra de `renderItemBonus`: só conta bonificação que resulta em valor.
  const hasAnyItemBonus = (recurrence?.items || []).some(
    (item: any) => getItemBonusValue(item) > 0
  )

  const contaAzulBillingRows = (recurrence?.billings || []).map((billing) => ({
    uuid: billing.uuid,
    cycle: billing.recurrenceCycle ?? undefined,
    dueDate: moment(billing.dueDate, 'YYYY-MM-DD').format('DD/MM/YYYY'),
    amount: formatMoney(billing.amountBilled),
    contaAzulInfo: billing.contaAzulInfo
  }))
  const [modalDialog, setModalDialog] = useState({
    isOpenDialogCancel: false,
    isOpenDetailInvoice: false,
    isOpenInstallmentDetail: false,
    isOpenLinkPlan: false,
    loading: false,
    uuid: ''
  })
  const isMobile = useMediaQuery('(max-width:600px)')
  const [isOpenModalEdit, setIsOpenModalEdit] = useState<boolean>(false)
  const [isOpenCancellationDialog, setIsOpenCancellationDialog] =
    useState<boolean>(false)
  const [isOpenCreditCardDialog, setIsOpenCreditCardDialog] =
    useState<boolean>(false)
  const [isOpenApplyAdjustmentDialog, setIsOpenApplyAdjustmentDialog] =
    useState<boolean>(false)
  const [isOpenCarneDialog, setIsOpenCarneDialog] = useState<boolean>(false)
  // SC-10941: o mesmo cartão que o CreditCardDetailsDialog mostra.
  const linkedCardNeedsReregistration =
    !!recurrence?.recurrenceCards?.[0]?.creditCard?.requiresReregistration
  const [isOpenContaAzul, setIsOpenContaAzul] = useState<boolean>(false)
  const [isOpenReactivateModal, setIsOpenReactivateModal] =
    useState<boolean>(false)
  const [isOpenDuplicate, setIsOpenDuplicate] = useState<boolean>(false)
  const [duplicateDefaults, setDuplicateDefaults] =
    useState<Partial<ChargeRecurrenceBodyModalForm>>()
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const { capabilities } = useAuthContext()

  // Coupon removal
  const { removeRecurrenceCoupon, loading: removingCoupon } =
    useRemoveRecurrenceCoupon()
  const [isOpenRemoveCouponDialog, setIsOpenRemoveCouponDialog] =
    useState(false)

  const handleRemoveCoupon = async () => {
    try {
      const result = await removeRecurrenceCoupon(recurrence.uuid)
      setIsOpenRemoveCouponDialog(false)

      if (result.quota_returned) {
        toast.success(
          `Cupom removido. +1 uso devolvido à quota do cupom ${recurrence.couponUsage?.couponCode}`
        )
      } else {
        toast.success(
          'Cupom removido. A quota não foi devolvida pois já houve pagamento nesta contratação'
        )
      }

      setReload(!reload)
      !!reloadTable && reloadTable((s) => !s)
    } catch (error) {
      toast.error(String(error))
    }
  }

  // Convert tags string to ChipTagData format for display
  const [recurrenceTags, setRecurrenceTags] = useState<ChipTagData[]>([])

  // Form for TagsPopover
  const { control, setValue } = useForm<TagForm>({
    defaultValues: {
      tags: []
    }
  })

  const handleCloseCancellationDialog = () => {
    setIsOpenCancellationDialog(!isOpenCancellationDialog)
  }

  const handleCloseApplyAdjustmentDialog = () => {
    setIsOpenApplyAdjustmentDialog(false)
  }

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setMenuAnchorEl(null)
  }

  const handleApplyAdjustmentSuccess = () => {
    setReload(!reload)
    !!reloadTable && reloadTable((s) => !s)
  }

  const canCancelNfses = !!CanLogical({ permission: 'nfses:cancel' })

  const cancelScheduled = async () => {
    const id = toast.loading('Cancelando agendamento de emissão da NF...')
    const response = await DataService({
      type: 'DELETE',
      url: `/v1/nfse_scheduleds/${recurrence?.nfsesScheduled?.uuid}`
    })

    setConfirmCancelScheduled(false)

    if (response.error) {
      toast.update(id, {
        render: response.detail_error || response.message,
        type: 'error',
        isLoading: false,
        autoClose: 5000
      })
      return
    }

    toast.update(id, {
      render: response.data.message,
      type: 'success',
      isLoading: false,
      autoClose: 5000
    })

    setReload(!reload)
    !!reloadTable && reloadTable((s) => !s)
  }

  const handleCloseEditCharge = () => {
    setIsOpenModalEdit(false)
  }

  const handleOpenEditCharge = () => {
    setIsOpenModalEdit(true)
  }

  const handleDuplicate = () => {
    setDuplicateDefaults(mapRecurrenceToDuplicateFormValues(recurrence))
    setIsOpenDuplicate(true)
    handleCloseMenu()
  }

  const handleCloseDetailInvoice = () => {
    setModalDialog((prevState) => ({
      ...prevState,
      isOpenDetailInvoice: false,
      uuid: ''
    }))

    setReload(!reload)
  }

  const handleOpenDetailInvoice = (uuid: string) => {
    setModalDialog((prevState) => ({
      ...prevState,
      isOpenDetailInvoice: true,
      uuid
    }))
  }

  const handleCloseInstallmentDetail = () => {
    setModalDialog((prevState) => ({
      ...prevState,
      isOpenInstallmentDetail: false,
      uuid: ''
    }))

    setReload(!reload)
  }

  const handleOpenInstallmentDetail = (uuid: string) => {
    setModalDialog((prevState) => ({
      ...prevState,
      isOpenInstallmentDetail: true,
      uuid
    }))
  }

  const handleRowAction = (uuid: string) => {
    // Verificar se o UUID corresponde a um installment ou billing
    const { grouped } = groupBillingsByInstallment(recurrence.billings || [])

    if (grouped[uuid]) {
      // É um installment - abrir modal de parcelamento
      handleOpenInstallmentDetail(uuid)
    } else {
      // É uma billing normal - abrir modal de cobrança
      handleOpenDetailInvoice(uuid)
    }
  }

  const copyUuid = () => {
    copy(recurrence.uuid)
    toast.success('Identificador copiado para a área de transferência')
  }

  // SC-10971: `amount` é o total da composição (inclui setup e itens de outros
  // ciclos). O card mostra o valor do ciclo corrente, calculado pelo backend.
  const cycleAmount = Number(recurrence?.cycleAmount ?? recurrence?.amount)
  const compositionDiffers =
    recurrence?.cycleAmount != null &&
    Math.abs(cycleAmount - Number(recurrence?.amount)) >= 0.01

  // O valor só varia entre ciclos se algum item tem janela própria (começa
  // depois do ciclo 1, tem número de ciclos, cadência maior que 1 ou herda o
  // teto do plano). Sem isso é o mesmo valor todo ciclo, e o tooltip diz só isso.
  const hasCycleWindowItems = (recurrence?.items || []).some(
    (item: any) =>
      Number(item?.start_cycle) > 1 ||
      Number(item?.total_cycles) > 0 ||
      Number(item?.cycle_interval) > 1 ||
      Number(item?.product?.total_cycles) > 0
  )
  const valorTooltip = compositionDiffers
    ? 'Valor cobrado no ciclo atual. Se o ciclo atual não tiver cobrança, mostra o valor do próximo ciclo cobrado. ' +
      'Itens cobrados só em alguns ciclos (como setup ou item a partir do ciclo N) fazem esse valor mudar ao longo do tempo. ' +
      '"Composição total" é a soma de todos os itens cadastrados.'
    : hasCycleWindowItems
    ? 'Valor cobrado no ciclo atual. Itens cobrados só em alguns ciclos (como setup ou item a partir do ciclo N) fazem esse valor mudar ao longo do tempo.'
    : 'Valor cobrado em cada ciclo da recorrência.'

  // Coluna "Parcelamento" da tabela de ciclos só quando algum ciclo foi parcelado.
  const hasInstallmentCycles =
    Object.keys(groupBillingsByInstallment(recurrence?.billings || []).grouped)
      .length > 0

  useEffect(() => {
    if (!isOpenModalRecurrenceDetail) {
      setIsOpenDuplicate(false)
      setDuplicateDefaults(undefined)
    }
  }, [isOpenModalRecurrenceDetail])

  useEffect(() => {
    if (!uuid && !isOpenModalRecurrenceDetail) return
    setLoadingModal(true)

    const getRecurrence = async (uuid: string) => {
      const response = await DataService({
        type: 'GET',
        url: `/v1/recurrences/${uuid}`
      })

      const { data, status } = await response
      if (status === 200) {
        const Recurrence = constructorRecurrences(data?.record)

        setRecurrence(Recurrence)

        const typeArray =
          Recurrence.paymentType
            .split(';')
            .filter((type) => type.length !== 0) || []
        const types = typeArray.map((type) => {
          return { label: `${getChargeEnum(type)}`, value: type }
        })
        setPaymentType(types)

        // Initialize recurrence tags for display
        const tags = convertTagsStringToArray(Recurrence.tags || '', [])
        setRecurrenceTags(tags)

        // Initialize tags in the form for TagsPopover
        const tagsForForm = tags.map((tag) => ({
          id: tag.id,
          name: tag.name,
          color: tag.color
        }))
        setValue('tags', tagsForForm)

        setLoadingModal(false)
      }
    }

    getRecurrence(uuid)
  }, [uuid, reload])

  return (
    <Modal
      handleClose={() => handleCloseDetailRecurrence(false)}
      open={isOpenModalRecurrenceDetail}
      maxWidth="1150px"
      width="100%"
      // `90vh` fixo, e não o antigo `calc(100%-200px)`: sem espaços em volta do
      // `-` a regra é inválida e o browser a descartava, deixando a altura por
      // conta do conteúdo — o modal abria curto no esqueleto e crescia quando
      // os dados chegavam.
      height="90vh"
      maxHeight="90vh"
      disablePortal={false}
      data-cy="recurrence-detail-modal"
    >
      <ModalBody>
        <ModalHeader
          onClose={() => handleCloseDetailRecurrence(false)}
          sx={modalHeaderMobileSx}
        >
          {/* SC-11275: mesmo header do detalhe da cobrança no celular. Antes
              empilhava em coluna e o X ficava solto entre as duas linhas. */}
          <Stack
            width="100%"
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={0}
            sx={headerGridMobileSx}
          >
            <Tooltip
              title={`Identificador: ${recurrence.uuid}`}
              placement="top"
              onClick={copyUuid}
              arrow
              PopperProps={{
                onClick: copyUuid,
                sx: { cursor: 'pointer' }
              }}
            >
              <Box sx={{ [mobile]: { gridArea: 'title', minWidth: 0 } }}>
                <Stack
                  sx={{ cursor: 'pointer' }}
                  direction="row"
                  alignItems="center"
                  gap={2}
                >
                  <IconContainer icon="event" />
                  <Typography variant="mdBold">
                    Detalhes da recorrência
                  </Typography>
                </Stack>
              </Box>
            </Tooltip>

            {!loadingModal ? (
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{ [mobile]: { display: 'contents' } }}
              >
                <Tooltip arrow title={getHoverText(recurrence.status).text}>
                  <Box
                    sx={{
                      width: '160px',
                      [theme.breakpoints.down(508)]: {
                        width: '120px'
                      },
                      [mobile]: { gridArea: 'status', width: '100%' }
                    }}
                  >
                    <Chip
                      variant={'' as any}
                      key={recurrence.status}
                      label={getStatus(recurrence.status)?.label}
                      sx={{
                        background: getStatus(recurrence.status)?.color,
                        color: getStatus(recurrence.status)?.fontColor,
                        height: '25px',
                        width: '160px',
                        [theme.breakpoints.down(508)]: {
                          width: '120px'
                        },
                        [mobile]: { width: '100%' }
                      }}
                    />
                  </Box>
                </Tooltip>
                <Can permission="recurrences:manage">
                  <IconButton
                    onClick={handleOpenMenu}
                    size="small"
                    sx={{ mr: '8px', [mobile]: { gridArea: 'more', mr: 0 } }}
                  >
                    <Icon icon="moreHoriz" colorSX="black" />
                  </IconButton>
                </Can>
              </Stack>
            ) : (
              <></>
            )}
          </Stack>
        </ModalHeader>

        {!loadingModal ? (
          <ModalContent>
            <S.RecurrenceContent>
              <DetailSummary
                pagador={
                  <PagadorFaixa
                    nome={recurrence?.customer?.name || '-'}
                    documento={formatCpfCnpj(
                      recurrence?.customer?.document || ''
                    )}
                    email={recurrence?.customer?.email || ''}
                    telefone={formatPhone(recurrence?.customer?.phone || '')}
                    href={
                      recurrence?.customer?.uuid
                        ? `/clientes/${recurrence.customer.uuid}`
                        : undefined
                    }
                  />
                }
                valor={
                  <>
                    {recurrence?.couponUsage &&
                    !recurrence.couponUsage.removedAt ? (
                      <CouponValueDisplay
                        originalAmount={cycleAmount}
                        finalAmount={
                          cycleAmount -
                          Number(recurrence.couponUsage.appliedDiscountAmount)
                        }
                        couponUsage={recurrence.couponUsage}
                        canRemove={true}
                        onRemoveClick={() => setIsOpenRemoveCouponDialog(true)}
                        perCycleLabel="por ciclo"
                      />
                    ) : (
                      <Typography
                        sx={{
                          fontSize: '24px',
                          fontWeight: 600,
                          letterSpacing: '-0.02em',
                          fontVariantNumeric: 'tabular-nums'
                        }}
                      >
                        {formatMoney(cycleAmount)}
                      </Typography>
                    )}
                    {/* SC-10971: o valor acima é o do ciclo corrente. Quando a
                        composição tem itens que não entram nele (setup, item a
                        partir do ciclo N), o total cadastrado fica como apoio,
                        para não sumir de vista. */}
                    {compositionDiffers && (
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: theme.palette.neutral[400],
                          fontVariantNumeric: 'tabular-nums'
                        }}
                      >
                        Composição total:{' '}
                        {formatMoney(Number(recurrence.amount))}
                      </Typography>
                    )}
                  </>
                }
                valorTooltip={valorTooltip}
                cadencia={`${
                  recurrence?.frequency_description ??
                  describeFrequency(
                    recurrence?.frequency as string,
                    recurrence?.frequency_type,
                    recurrence?.frequency_number
                  )
                } · vence dia ${recurrence?.dueDay}`}
                destaques={
                  <>
                    {recurrence.hasBonus && (
                      <Chip
                        size="small"
                        color="success"
                        sx={{ fontSize: '11px', height: '22px' }}
                        label={`Bonificação ${
                          recurrence.bonusSettings?.type == 'value'
                            ? formatMoney(recurrence.bonusSettings.amount)
                            : `${Number(recurrence.bonusSettings?.amount)
                                .toFixed(2)
                                .replace('.', ',')}%`
                        }`}
                      />
                    )}
                    {/* `current_cycle` vem 0 enquanto a primeira cobrança não
                        foi emitida — "Ciclo 0 de 12" é um ciclo que não existe.
                        Nesse caso dizemos o que está acontecendo, e o tooltip
                        explica quando a fatura sai. "Indeterminado" por extenso
                        porque o ∞ vira um risquinho no tamanho de um chip. */}
                    <Tooltip
                      title={
                        !recurrence?.currentCycle && recurrence?.firstDueDate
                          ? `A primeira fatura vence em ${moment(
                              recurrence.firstDueDate
                            ).format('DD/MM/YYYY')}${
                              recurrence?.issue_days &&
                              recurrence?.issue_billing_type ===
                                'days_before_due_date'
                                ? ` e será emitida ${recurrence.issue_days} dias antes.`
                                : '.'
                            }`
                          : ''
                      }
                    >
                      {/* span porque o Chip da library não encaminha os
                          handlers de mouse que o tooltip precisa. */}
                      <span style={{ display: 'inline-flex' }}>
                        <Chip
                          size="small"
                          sx={{ fontSize: '11px', height: '22px' }}
                          label={
                            !recurrence?.currentCycle
                              ? 'Aguardando 1º ciclo'
                              : `Ciclo ${recurrence.currentCycle} de ${
                                  recurrence?.totalCycles === 0
                                    ? 'Indeterminado'
                                    : recurrence?.totalCycles
                                }`
                          }
                        />
                      </span>
                    </Tooltip>
                  </>
                }
              >
                {/* SC-10999: de qual empresa é a recorrência, na visão
                    multi-empresas */}
                <DetailCompanyCell recordUuid={recurrence?.uuid} />

                {recurrence?.issue_days && (
                  <Celula
                    label={
                      recurrence?.issue_billing_type === 'specific_day'
                        ? 'Dia de emissão'
                        : 'Emitir antes do venc.'
                    }
                  >
                    {recurrence?.issue_billing_type === 'specific_day'
                      ? `Dia ${recurrence?.issue_days}`
                      : `${recurrence?.issue_days} dias`}
                  </Celula>
                )}

                {recurrence.expirationField && (
                  <Celula label="Dias para expirar">
                    {String(recurrence?.expirationField)}
                  </Celula>
                )}

                {recurrence.hasBonus && (
                  <Celula label="Bonificação">
                    {`${
                      recurrence.bonusSettings?.type == 'value'
                        ? formatMoney(recurrence.bonusSettings.amount)
                        : Number(recurrence.bonusSettings?.amount)
                            .toFixed(2)
                            .replace('.', ',')
                    }${
                      recurrence.bonusSettings?.type == 'percentage' ? '%' : ''
                    } ${(() => {
                      // Mostra a faixa de ciclos que a bonificação cobre, e não
                      // a contagem crua: começando no ciclo N por C ciclos, ela
                      // termina em N + C - 1, limitado ao último da recorrência.
                      const start = recurrence.bonusSettings?.startCycle || 1
                      const count = recurrence.bonusSettings?.cyclesCount || 0
                      const recTotal = recurrence.totalCycles || 0
                      if (count <= 0) return `a partir do ciclo ${start}`
                      let end = start + count - 1
                      if (recTotal > 0) end = Math.min(end, recTotal)
                      return start === end
                        ? `no ciclo ${start}`
                        : `do ciclo ${start} ao ${end}`
                    })()}`}
                  </Celula>
                )}

                {recurrence.bonusCarryover && (
                  <Celula label="Saldo de bonificação">
                    {/* Só o saldo atual; o detalhe fica no accordion
                        "Saldo de bonificação". */}
                    {recurrence.bonusCarryover.expired
                      ? 'Expirado'
                      : `${formatMoney(
                          recurrence.bonusCarryover.remaining
                        )} restante`}
                  </Celula>
                )}

                {recurrence?.discountPolicy &&
                recurrence?.discounts.length > 0 ? (
                  recurrence?.discounts.map((discount, index) => (
                    <Celula
                      key={index}
                      label={
                        recurrence.discounts.length > 1
                          ? `Desconto ${index + 1}`
                          : 'Desconto'
                      }
                    >
                      {`${
                        discount.policy == 'percentage'
                          ? `${Number(discount.amount)
                              .toFixed(2)
                              .replace('.', ',')}%`
                          : formatMoney(Number(discount.amount))
                      } ${
                        discount.days === 0
                          ? 'no vencimento'
                          : `até ${discount.days} dia(s) antes`
                      }`}
                    </Celula>
                  ))
                ) : (
                  <Celula label="Desconto">
                    <NaoConfigurado />
                  </Celula>
                )}

                <Celula label="Multa">
                  {recurrence.interestFine === 'no_interest' ||
                  Number(recurrence.interestFine).toFixed(2) === '0.00' ? (
                    <NaoConfigurado />
                  ) : (
                    `${Number(recurrence.interestFine)
                      .toFixed(2)
                      .replace('.', ',')}%`
                  )}
                </Celula>

                <Celula label="Juros">
                  {recurrence.interestFine === 'no_interest' ||
                  Number(recurrence.interestFee).toFixed(2) === '0.00' ? (
                    <NaoConfigurado />
                  ) : (
                    `${Number(recurrence.interestFee)
                      .toFixed(2)
                      .replace('.', ',')}% ao mês`
                  )}
                </Celula>

                <Celula label="Reajuste">
                  {recurrence?.adjustmentScheduled ? (
                    `${
                      recurrence.adjustmentScheduled.adjustment_type ===
                      'customized'
                        ? `${Number(
                            recurrence.adjustmentScheduled.percentage_value
                          )
                            .toFixed(2)
                            .replace('.', ',')}%`
                        : getAdjustmentTypeLabel(
                            recurrence.adjustmentScheduled.adjustment_type
                          )
                    } / ${getFrequency(
                      recurrence.adjustmentScheduled.frequency
                    )}`
                  ) : (
                    <NaoConfigurado />
                  )}
                </Celula>

                {/* Largura inteira só quando há muitos meios: com cinco
                    chips eles ocupavam três linhas ao dividir espaço com o
                    Reajuste, mas com um ou dois a linha cheia desperdiça o
                    espaço que sobra ao lado. */}
                <Celula
                  label="Meios de pagamento"
                  larga={meiosDePagamentoVisiveis.length > 2}
                >
                  <ListaChips>
                    {paymentType.map((item, index) => {
                      if (!item.value || item.value === 'boleto_pix') {
                        return null
                      }

                      if (item.value === 'credit_card_recurring') {
                        return (
                          <Box
                            display="flex"
                            alignItems="center"
                            gap="6px"
                            key={index}
                          >
                            <Chip
                              variant="payment"
                              label={item.label.toUpperCase()}
                            />
                            {recurrence?.recurrenceCards?.length > 0 && (
                              <Tooltip
                                enterTouchDelay={0}
                                arrow
                                placement="top"
                                title={
                                  linkedCardNeedsReregistration
                                    ? `Recadastro necessário. ${REQUIRES_REREGISTRATION_TOOLTIP}`
                                    : 'Clique para ver detalhes do cartão'
                                }
                              >
                                <Wrapper
                                  onClick={() =>
                                    setIsOpenCreditCardDialog(true)
                                  }
                                  style={{
                                    cursor: 'pointer',
                                    display: 'inline-flex'
                                  }}
                                >
                                  <Icon
                                    icon="creditCardChecked"
                                    sx={{
                                      color: linkedCardNeedsReregistration
                                        ? theme.palette.warning.main
                                        : theme.palette.neutral[200],
                                      '&:hover': {
                                        color: theme.palette.primary.main
                                      }
                                    }}
                                  />
                                </Wrapper>
                              </Tooltip>
                            )}
                          </Box>
                        )
                      }

                      return (
                        <Chip
                          key={index}
                          variant="payment"
                          label={item.label.toUpperCase()}
                        />
                      )
                    })}
                  </ListaChips>
                </Celula>

                {recurrenceTags.length > 0 && (
                  <Celula label="Tags" larga>
                    <ListaChips>
                      {recurrenceTags.map((tag) => (
                        <ChipTag
                          key={tag.id}
                          tag={tag}
                          // Sem onDelete: aqui é só exibição.
                        />
                      ))}
                    </ListaChips>
                  </Celula>
                )}

                {recurrence?.description && (
                  <Celula label="Descrição" larga>
                    <DescricaoRecolhivel texto={recurrence.description} />
                  </Celula>
                )}

                {recurrence?.paymentTypeDetails && (
                  <PaymentTypeDetailsView
                    paymentTypeDetails={recurrence.paymentTypeDetails}
                  />
                )}
              </DetailSummary>

              {recurrence?.cancellationReason?.reasonType && (
                <Box
                  display="flex"
                  flexWrap="wrap"
                  gap={1.25}
                  alignItems="end"
                  margin="10px 0px 10px 0px"
                >
                  <Stack direction="row">
                    <Typography
                      variant="smLight"
                      color={theme.palette.neutral[400]}
                    >
                      Motivo do cancelamento (
                    </Typography>
                    <Typography
                      onClick={handleCloseCancellationDialog}
                      sx={{
                        cursor: 'pointer',
                        textDecoration: 'underline'
                      }}
                      variant="smLight"
                      color={theme.palette.neutral[400]}
                    >
                      editar
                    </Typography>
                    <Typography
                      variant="smLight"
                      color={theme.palette.neutral[400]}
                    >
                      )
                    </Typography>
                  </Stack>
                  <S.Separator sx={{ marginTop: '4px' }} />
                  <Typography
                    variant="smBold"
                    display="flex"
                    color={theme.palette.error['300']}
                    alignItems="center"
                  >
                    {reasonTypeMapper[recurrence.cancellationReason.reasonType]}
                  </Typography>
                </Box>
              )}
              {recurrence?.cancellationReason?.notes && (
                <Stack gap={1} margin="10px 0px 10px 0px" mt="16px">
                  <Typography
                    variant="smLight"
                    color={theme.palette.neutral[400]}
                  >
                    Observações do cancelamento
                  </Typography>
                  <Typography
                    color={theme.palette.neutral[400]}
                    sx={{ whiteSpace: 'pre-line' }}
                  >
                    {recurrence?.cancellationReason?.notes}
                  </Typography>
                </Stack>
              )}
            </S.RecurrenceContent>

            <S.AccordionWrapper>
              <div>
                <Accordion
                  defaultExpanded
                  sx={{
                    border: '1px solid #E8EBE8',
                    borderLeft: '3px solid',
                    borderLeftColor: 'primary.main',
                    borderRadius: '8px !important',
                    backgroundColor: '#FAFBFA',
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': {
                      margin: 0,
                      backgroundColor: '#fff'
                    }
                  }}
                >
                  <AccordionSummary
                    aria-controls="payerDetails-content"
                    id="payerDetails"
                  >
                    {cardTittle(
                      'Composição da recorrência',
                      'paid',
                      `${theme.palette.primary.main}`
                    )}
                  </AccordionSummary>
                  <AccordionDetails>
                    <div>
                      <Box maxHeight="200px" overflow="auto">
                        <Table
                          mobileCards
                          header={[
                            { id: 'item', label: 'Plano/Item' },
                            { id: 'amountUnit', label: 'Valor' },
                            // Sem nenhum item bonificado a coluna seria uma
                            // fileira de traços ocupando espaço à toa.
                            ...(hasAnyItemBonus
                              ? [{ id: 'bonus', label: 'Bonificação' }]
                              : []),
                            { id: 'quantity', label: 'Qtd.' },
                            { id: 'amountBilled', label: 'Total' },
                            { id: 'cycles', label: 'Ciclos' }
                          ]}
                          hoverInRow
                          rows={
                            recurrence.items && Array.isArray(recurrence.items)
                              ? compositionDetailMapper(
                                  recurrence.items,
                                  true,
                                  hasAnyItemBonus
                                )
                              : []
                          }
                          select={{ useSelect: false }}
                          hiddePagination
                        />
                      </Box>
                    </div>
                  </AccordionDetails>
                </Accordion>
              </div>
              <div>
                <Accordion
                  defaultExpanded
                  sx={{
                    border: '1px solid #E8EBE8',
                    borderLeft: '3px solid',
                    borderLeftColor: 'primary.main',
                    borderRadius: '8px !important',
                    backgroundColor: '#FAFBFA',
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': {
                      margin: 0,
                      backgroundColor: '#fff'
                    }
                  }}
                >
                  <AccordionSummary
                    aria-controls="payerDetails-content"
                    id="payerDetails"
                  >
                    {cardTittle(
                      'Ciclos',
                      'cached',
                      `${theme.palette.primary.main}`
                    )}
                  </AccordionSummary>
                  <AccordionDetails>
                    {renderCurrentCycleHighlight()}
                    <Box maxHeight="300px" overflow="auto">
                      <Table
                        mobileCards
                        handleChangePage={() => null}
                        handleChangeRowsPerPage={() => null}
                        header={[
                          { id: 'dueDate', label: 'Vencimento' },
                          { id: 'datePaid', label: 'Pagamento' },
                          { id: 'amountBilled', label: 'Valor' },
                          { id: 'amountPaid', label: 'Valor Pago' },
                          ...(hasInstallmentCycles
                            ? [{ id: 'installment', label: 'Parcelamento' }]
                            : []),
                          { id: 'status', label: 'Status' },
                          { id: 'actions', label: '' }
                        ]}
                        hiddePagination={true}
                        page={1}
                        rows={convertToRows(recurrence.billings || [])}
                        rowAction={handleRowAction}
                        rowsPerPage={Infinity}
                        totalCount={0}
                        select={{ useSelect: false }}
                        hideLastRowSeparator
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </div>
              {recurrence.bonusCarryover && (
                <div>
                  <Accordion
                    defaultExpanded
                    sx={{
                      border: '1px solid #E8EBE8',
                      borderLeft: '3px solid',
                      borderLeftColor: 'primary.main',
                      borderRadius: '8px !important',
                      backgroundColor: '#FAFBFA',
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': {
                        margin: 0,
                        backgroundColor: '#fff'
                      }
                    }}
                  >
                    <AccordionSummary
                      aria-controls="bonusCarryover-content"
                      id="bonusCarryover"
                    >
                      {cardTittle(
                        'Saldo de bonificação',
                        'cardGiftcard',
                        `${theme.palette.primary.main}`
                      )}
                    </AccordionSummary>
                    <AccordionDetails>
                      <BonusCarryoverDetail
                        carryover={recurrence.bonusCarryover}
                        billings={recurrence.billings || []}
                        onOpenBilling={handleOpenDetailInvoice}
                      />
                    </AccordionDetails>
                  </Accordion>
                </div>
              )}
              <NegativateDetail
                recurrenceNegativationData={recurrence.negativation}
              />
              {recurrence?.adjustmentScheduled && (
                <Accordion
                  sx={{
                    border: '1px solid #E8EBE8',
                    borderLeft: '3px solid',
                    borderLeftColor: 'primary.main',
                    borderRadius: '8px !important',
                    backgroundColor: '#FAFBFA',
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': {
                      margin: 0,
                      backgroundColor: '#fff'
                    }
                  }}
                >
                  <AccordionSummary
                    aria-controls="adjustmentDetails-content"
                    id="adjustmentDetails"
                  >
                    {cardTittle(
                      'Reajuste',
                      'add',
                      `${theme.palette.primary.main}`
                    )}
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      display="flex"
                      gap={1.25}
                      alignItems="end"
                      margin={'10px 0px 10px 0px'}
                    >
                      <Typography
                        variant="smLight"
                        color={theme.palette.neutral[400]}
                      >
                        Tipo de reajuste
                      </Typography>
                      <S.Separator sx={{ marginTop: '4px' }} />
                      <Typography variant="smBold">
                        {getAdjustmentTypeLabel(
                          recurrence.adjustmentScheduled.adjustment_type
                        )}
                      </Typography>
                    </Box>
                    <Box
                      display="flex"
                      gap={1.25}
                      alignItems="end"
                      margin={'10px 0px 10px 0px'}
                    >
                      <Typography
                        variant="smLight"
                        color={theme.palette.neutral[400]}
                      >
                        Frequência
                      </Typography>
                      <S.Separator sx={{ marginTop: '4px' }} />
                      <Typography variant="smBold">
                        {getFrequency(recurrence.adjustmentScheduled.frequency)}
                      </Typography>
                    </Box>
                    {recurrence.adjustmentScheduled.adjustment_type ===
                      'customized' &&
                      recurrence.adjustmentScheduled.percentage_value && (
                        <Box
                          display="flex"
                          gap={1.25}
                          alignItems="end"
                          margin={'10px 0px 10px 0px'}
                        >
                          <Typography
                            variant="smLight"
                            color={theme.palette.neutral[400]}
                          >
                            Percentual
                          </Typography>
                          <S.Separator sx={{ marginTop: '4px' }} />
                          <Typography variant="smBold">
                            {Number(
                              recurrence.adjustmentScheduled.percentage_value
                            )
                              .toFixed(2)
                              .replace('.', ',')}
                            %
                          </Typography>
                        </Box>
                      )}
                    {recurrence.adjustmentScheduled.next_adjustment_date && (
                      <Box
                        display="flex"
                        gap={1.25}
                        alignItems="end"
                        margin={'10px 0px 10px 0px'}
                      >
                        <Typography
                          variant="smLight"
                          color={theme.palette.neutral[400]}
                        >
                          Próximo reajuste
                        </Typography>
                        <S.Separator sx={{ marginTop: '4px' }} />
                        <Typography variant="smBold">
                          {moment(
                            recurrence.adjustmentScheduled.next_adjustment_date,
                            'YYYY-MM-DD'
                          ).format('DD/MM/YYYY')}
                        </Typography>
                      </Box>
                    )}
                    <Box
                      display="flex"
                      gap={1.25}
                      alignItems="end"
                      margin={'10px 0px 10px 0px'}
                    >
                      <Typography
                        variant="smLight"
                        color={theme.palette.neutral[400]}
                      >
                        Reajustar automaticamente
                      </Typography>
                      <S.Separator sx={{ marginTop: '4px' }} />
                      <Typography variant="smBold">
                        {recurrence.adjustmentScheduled.automatic_application
                          ? 'Sim'
                          : 'Não'}
                      </Typography>
                    </Box>
                    <Box
                      display="flex"
                      gap={1.25}
                      alignItems="end"
                      margin={'10px 0px 10px 0px'}
                    >
                      <Typography
                        variant="smLight"
                        color={theme.palette.neutral[400]}
                      >
                        Aplicado em
                      </Typography>
                      <S.Separator sx={{ marginTop: '4px' }} />
                      <Typography variant="smBold">
                        {recurrence.adjustmentScheduled.billing_item_uuids
                          ?.length
                          ? `${
                              recurrence.adjustmentScheduled.billing_item_uuids
                                .length
                            } de ${recurrence.items?.length ?? 0} itens`
                          : 'Todos os itens'}
                      </Typography>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
              <Accordion
                data-cy="history-accordion"
                sx={{
                  border: '1px solid #E8EBE8',
                  borderLeft: '3px solid',
                  borderLeftColor: 'primary.main',
                  borderRadius: '8px !important',
                  backgroundColor: '#FAFBFA',
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    margin: 0,
                    backgroundColor: '#fff'
                  }
                }}
              >
                <AccordionSummary
                  aria-controls="changesHistory-content"
                  id="historyChanges"
                  data-cy="history-accordion-summary"
                >
                  {cardTittle(
                    'Histórico',
                    'article',
                    `${theme.palette.primary.main}`
                  )}
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{
                      // Era um flex container: a tabela encolhia ao conteúdo e
                      // deixava metade da largura vazia à direita.
                      width: '100%',
                      overflowX: 'auto',
                      [theme.breakpoints.down(450)]: {
                        maxWidth: '40vh'
                      }
                    }}
                  >
                    <Table
                      mobileCards
                      header={[
                        { id: 'action', label: 'Ação' },
                        { id: 'description', label: 'Descrição' },
                        { id: 'date', label: 'Data' },
                        { id: 'user', label: 'Usuário' }
                      ]}
                      rows={historicChangesMapper(
                        recurrence.historic as Historic
                      )}
                      select={{ useSelect: false }}
                      hiddePagination
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
              {Object.keys(recurrence?.metadata).length ? (
                <>
                  <div>
                    <Accordion
                      data-cy="internal-identifier-accordion"
                      sx={{
                        border: '1px solid #E8EBE8',
                        borderLeft: '3px solid',
                        borderLeftColor: 'primary.main',
                        borderRadius: '8px !important',
                        backgroundColor: '#FAFBFA',
                        boxShadow: 'none',
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': {
                          margin: 0,
                          backgroundColor: '#fff'
                        }
                      }}
                    >
                      <AccordionSummary
                        aria-controls="payerDetails-content"
                        id="payerDetails"
                        data-cy="internal-identifier-accordion-summary"
                      >
                        {cardTittle(
                          'Identificador interno',
                          'bookmark',
                          `${theme.palette.primary.main}`
                        )}
                      </AccordionSummary>
                      <AccordionDetails>
                        {Object.keys(recurrence?.metadata).map((field) => (
                          <>
                            <div>
                              <Box
                                display="flex"
                                gap={1.25}
                                alignItems="end"
                                margin={'10px 0px 10px 0px'}
                              >
                                <Typography
                                  variant="smLight"
                                  color={theme.palette.neutral[400]}
                                >
                                  Nome do campo
                                </Typography>
                                <S.Separator sx={{ marginTop: '4px' }} />
                                <Typography variant="smBold">
                                  {field}
                                </Typography>
                              </Box>
                            </div>
                            <div>
                              <Box
                                display="flex"
                                gap={1.25}
                                alignItems="end"
                                margin={'10px 0px 10px 0px'}
                              >
                                <Typography
                                  variant="smLight"
                                  color={theme.palette.neutral[400]}
                                >
                                  Informação
                                </Typography>
                                <S.Separator sx={{ marginTop: '4px' }} />
                                <Typography variant="smBold">
                                  {formatMetadataValue(
                                    recurrence?.metadata[field]
                                  )}
                                </Typography>
                              </Box>
                            </div>
                          </>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  </div>
                </>
              ) : (
                <></>
              )}
              {recurrence?.nfsesScheduled && (
                <div>
                  <Accordion
                    sx={{
                      border: '1px solid #E8EBE8',
                      borderLeft: '3px solid',
                      borderLeftColor: 'primary.main',
                      borderRadius: '8px !important',
                      backgroundColor: '#FAFBFA',
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': {
                        margin: 0,
                        backgroundColor: '#fff'
                      }
                    }}
                  >
                    <AccordionSummary
                      aria-controls="payerDetails-content"
                      id="payerDetails"
                    >
                      {cardTittle(
                        'Nota fiscal',
                        'assignment',
                        `${theme.palette.primary.main}`
                      )}
                    </AccordionSummary>
                    <AccordionDetails>
                      <>
                        <Box
                          display="flex"
                          gap={1.25}
                          alignItems="end"
                          margin={'10px 0px 10px 0px'}
                        >
                          <Typography
                            variant="smLight"
                            color={theme.palette.neutral[400]}
                          >
                            Prestador de serviço
                          </Typography>
                          <S.Separator sx={{ marginTop: '4px' }} />
                          <Typography variant="smBold">
                            {recurrence?.nfsesScheduled?.nfse_issuer
                              ?.legal_name || ''}
                          </Typography>
                        </Box>
                        <Box
                          display="flex"
                          gap={1.25}
                          alignItems="end"
                          margin={'10px 0px 10px 0px'}
                        >
                          <Typography
                            variant="smLight"
                            color={theme.palette.neutral[400]}
                          >
                            ISS retido
                          </Typography>
                          <S.Separator sx={{ marginTop: '4px' }} />
                          <Typography variant="smBold">
                            {recurrence?.nfsesScheduled?.iss_retention === true
                              ? 'Sim'
                              : 'Não'}
                          </Typography>
                        </Box>
                        <Box
                          display="flex"
                          gap={1.25}
                          alignItems="end"
                          margin={'10px 0px 10px 0px'}
                        >
                          <Typography
                            variant="smLight"
                            color={theme.palette.neutral[400]}
                          >
                            Emissão
                          </Typography>
                          <S.Separator sx={{ marginTop: '4px' }} />
                          <Typography variant="smBold">
                            {
                              issueWhenEnum[
                                recurrence?.nfsePolicy as string as keyof typeof issueWhenEnum
                              ]
                            }
                          </Typography>
                        </Box>
                        {((recurrence?.nfsesScheduled as any)
                          ?.configuration_type === 1 ||
                          (recurrence?.nfsesScheduled as any)
                            ?.configuration_type === 'specific_day_of_month' ||
                          (recurrence?.nfsesScheduled as any)?.issue_mode ===
                            'specific_day') && (
                          <>
                            <Box
                              display="flex"
                              gap={1.25}
                              alignItems="end"
                              margin={'10px 0px 10px 0px'}
                            >
                              <Typography
                                variant="smLight"
                                color={theme.palette.neutral[400]}
                              >
                                Dia do mês
                              </Typography>
                              <S.Separator sx={{ marginTop: '4px' }} />
                              <Typography variant="smBold">
                                {(recurrence?.nfsesScheduled as any)
                                  ?.day_of_month ||
                                  (recurrence?.nfsesScheduled as any)
                                    ?.specific_day ||
                                  ''}
                              </Typography>
                            </Box>
                            <Box
                              display="flex"
                              gap={1.25}
                              alignItems="end"
                              margin={'10px 0px 10px 0px'}
                            >
                              <Typography
                                variant="smLight"
                                color={theme.palette.neutral[400]}
                              >
                                Mês de emissão
                              </Typography>
                              <S.Separator sx={{ marginTop: '4px' }} />
                              <Typography variant="smBold">
                                {(() => {
                                  const relVal = (
                                    recurrence?.nfsesScheduled as any
                                  )?.due_date_relation as string
                                  const mapStr: Record<string, string> = {
                                    two_months_before:
                                      'Dois meses antes do vencimento',
                                    month_before: 'Mês anterior ao vencimento',
                                    same_month: 'Mês do vencimento',
                                    month_after: 'Mês seguinte ao vencimento',
                                    two_months_after:
                                      'Dois meses depois do vencimento'
                                  }
                                  if (relVal) return mapStr[relVal] || ''
                                  const off = (
                                    recurrence?.nfsesScheduled as any
                                  )?.specific_month_offset
                                  const offMap: Record<string, string> = {
                                    '-2': mapStr.two_months_before,
                                    '-1': mapStr.month_before,
                                    '0': mapStr.same_month,
                                    '1': mapStr.month_after,
                                    '2': mapStr.two_months_after
                                  }
                                  return offMap[String(off)] || ''
                                })()}
                              </Typography>
                            </Box>
                          </>
                        )}

                        <Box
                          display="flex"
                          gap={1.25}
                          alignItems="end"
                          margin={'10px 0px 10px 0px'}
                        >
                          <Typography
                            variant="smLight"
                            color={theme.palette.neutral[400]}
                          >
                            Item de serviço
                          </Typography>
                          <S.Separator sx={{ marginTop: '4px' }} />
                          <Typography variant="smBold">
                            {
                              recurrence?.nfsesScheduled?.nfse_issuer_service
                                ?.code
                            }{' '}
                            - {''}
                            {
                              recurrence?.nfsesScheduled?.nfse_issuer_service
                                ?.description
                            }
                          </Typography>
                        </Box>
                        <Box
                          display="flex"
                          gap={1.25}
                          alignItems="end"
                          margin={'10px 0px 10px 0px'}
                        >
                          <Typography
                            variant="smLight"
                            color={theme.palette.neutral[400]}
                          >
                            Valor da nota
                          </Typography>
                          <S.Separator sx={{ marginTop: '4px' }} />
                          <Typography variant="smBold">
                            {
                              amountTypeEnum[
                                recurrence?.nfsesScheduled
                                  ?.amount_type as keyof typeof amountTypeEnum
                              ]
                            }{' '}
                            {recurrence?.nfsesScheduled?.amount
                              ? `- ${formatMoney(
                                  Number(recurrence?.nfsesScheduled?.amount)
                                )}`
                              : ''}
                          </Typography>
                        </Box>
                        <Box
                          display="flex"
                          gap={1.25}
                          alignItems="end"
                          margin={'10px 0px 10px 0px'}
                        >
                          <Typography
                            variant="smLight"
                            color={theme.palette.neutral[400]}
                          >
                            Natureza de operação
                          </Typography>
                          <S.Separator sx={{ marginTop: '4px' }} />
                          <Typography variant="smBold">
                            {
                              natureOperationEnum[
                                recurrence?.nfsesScheduled
                                  ?.nature_operation as keyof typeof natureOperationEnum
                              ]
                            }
                          </Typography>
                        </Box>
                        {recurrence?.nfsesScheduled?.copy_description ? (
                          <Box
                            display="flex"
                            gap={1.25}
                            alignItems="end"
                            margin={'10px 0px 10px 0px'}
                          >
                            <Typography
                              variant="smLight"
                              color={theme.palette.neutral[400]}
                            >
                              Copiar descrição da cobrança
                            </Typography>
                            <S.Separator />
                            <Typography variant="smBold">
                              <Typography variant="smBold">
                                {recurrence?.nfsesScheduled?.copy_description
                                  ? 'Sim'
                                  : 'Não'}
                              </Typography>
                            </Typography>
                          </Box>
                        ) : (
                          recurrence.nfsesScheduled?.description && (
                            <Box display="flex" flexDirection="column">
                              <Typography
                                variant="smLight"
                                color={theme.palette.neutral[400]}
                                mb="6px"
                              >
                                Descrição
                              </Typography>
                              <Typography variant="smBold">
                                {recurrence.nfsesScheduled?.description}
                              </Typography>
                            </Box>
                          )
                        )}

                        {canCancelNfses && recurrence?.nfsesScheduled?.uuid && (
                          <Box mt={2} display="flex" justifyContent="flex-end">
                            <Button
                              variantButton="outlined"
                              onClick={() => setConfirmCancelScheduled(true)}
                            >
                              Cancelar agendamento de emissão da NF
                            </Button>
                          </Box>
                        )}

                        <Dialog
                          icon="info"
                          title="Cancelar agendamento de emissão da NF?"
                          description="Todas as notas fiscais programadas desta recorrência serão canceladas e não serão emitidas. Essa ação não pode ser desfeita."
                          setIsOpenDialog={setConfirmCancelScheduled}
                          isOpenDialog={confirmCancelScheduled}
                          cancelButton
                          cancelButtonLabel="Voltar"
                          PaperProps={{
                            sx: { maxWidth: '440px', width: '100%' }
                          }}
                        >
                          <Button onClick={cancelScheduled} fullWidth>
                            Cancelar agendamento
                          </Button>
                        </Dialog>
                      </>
                    </AccordionDetails>
                  </Accordion>
                </div>
              )}
            </S.AccordionWrapper>
          </ModalContent>
        ) : (
          <ModalContent>
            {/* O esqueleto acompanha o cabeçalho novo: faixa do pagador,
                cartão do valor à esquerda com a grade de campos à direita e os
                accordions abaixo. O antigo desenhava dois blocos com avatar e
                <hr>, uma forma que já não existe — na troca a tela pulava. */}
            {/* Esqueleto propositalmente grosseiro: blocos largos que só
                marcam a região de cada bloco de conteúdo. Reproduzir campo a
                campo faz o carregamento parecer conteúdo real e chama mais
                atenção do que a tela que está chegando. */}
            {/* Caixa neutra: a borda verde de acento é do conteúdo real e,
                no esqueleto, virava a única cor da tela. */}
            <Box
              sx={{
                padding: '16px',
                border: '1px solid #E8EBE8',
                borderRadius: '8px',
                backgroundColor: '#fff'
              }}
            >
              <Stack gap="16px">
                <Box
                  alignItems="center"
                  display="flex"
                  gap="14px"
                  paddingBottom="16px"
                  sx={{ borderBottom: '1px solid #E8EBE8' }}
                >
                  <Skeleton height="38px" variant="circular" width="38px" />
                  <Skeleton height="24px" variant="rounded" width="45%" />
                </Box>

                <Box
                  display="grid"
                  gap="18px"
                  sx={{
                    gridTemplateColumns: '200px 1fr',
                    [theme.breakpoints.down(760)]: {
                      gridTemplateColumns: '1fr'
                    }
                  }}
                >
                  <Stack
                    gap="10px"
                    paddingRight="20px"
                    sx={{ borderRight: '1px solid #E8EBE8' }}
                  >
                    <Skeleton height="34px" variant="rounded" />
                    <Skeleton height="20px" variant="rounded" width="70%" />
                  </Stack>

                  <Box
                    display="grid"
                    gap="16px"
                    sx={{
                      gridTemplateColumns:
                        'repeat(auto-fit, minmax(160px, 1fr))'
                    }}
                  >
                    <Skeleton height="30px" variant="rounded" />
                    <Skeleton height="30px" variant="rounded" />
                    <Skeleton height="30px" variant="rounded" />
                    <Skeleton height="30px" variant="rounded" />

                    {/* Meios de pagamento e tags vêm como chips e ocupam a
                        linha inteira, então o esqueleto reserva a faixa toda. */}
                    <Box
                      display="flex"
                      flexWrap="wrap"
                      gap="8px"
                      sx={{ gridColumn: '1 / -1' }}
                    >
                      <Skeleton height="28px" variant="rounded" width="110px" />
                      <Skeleton height="28px" variant="rounded" width="90px" />
                      <Skeleton height="28px" variant="rounded" width="140px" />
                      <Skeleton height="28px" variant="rounded" width="120px" />
                    </Box>
                  </Box>
                </Box>
              </Stack>
            </Box>

            <Stack gap="12px" marginTop="16px">
              {/* A composição abre expandida, então o esqueleto reserva a
                  altura da tabela — senão o resto do modal salta ao carregar. */}
              <Box
                sx={{
                  border: '1px solid #E8EBE8',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  padding: '16px'
                }}
              >
                <Box alignItems="center" display="flex" gap="12px">
                  <Skeleton height="35px" variant="rounded" width="35px" />
                  <Skeleton height="22px" variant="rounded" width="220px" />
                </Box>

                <Stack gap="12px" marginTop="20px">
                  <Skeleton height="26px" variant="rounded" />
                  <Skeleton height="26px" variant="rounded" />
                  <Skeleton height="26px" variant="rounded" />
                </Stack>
              </Box>

              {/* Um único bloco fechado no lugar dos accordions restantes: com
                  dois, o esqueleto passava da altura do modal e nascia com
                  barra de rolagem, que o conteúdo real não tem de saída. */}
              <Skeleton height="60px" variant="rounded" />
            </Stack>
          </ModalContent>
        )}

        {(recurrence?.status === 'pending' ||
          recurrence?.status === 'active' ||
          recurrence?.status === 'cancelled') && (
          <ModalFooter>
            <Stack
              width="100%"
              flexDirection={isMobile ? 'column' : 'row'}
              justifyContent="flex-end"
              alignItems={isMobile ? 'stretch' : 'center'}
              gap={1}
            >
              {/* TagsPopover positioned to the right */}
              <Stack direction="row" alignItems="center">
                <TagsPopover
                  control={control}
                  name="tags"
                  saveMode="immediate"
                  saveContext={{
                    taggableType: 'Recurrence',
                    taggableUuid: recurrence?.uuid || ''
                  }}
                  onSave={async (tags) => {
                    // Update local state with new tags
                    const updatedTags = tags.map((tag) => ({
                      id: tag.id,
                      name: tag.name,
                      color: tag.color || '#000000'
                    }))
                    setRecurrenceTags(updatedTags)

                    // Show success notification
                    toast.success('Tags atualizadas com sucesso!')
                    // Reload the recurrence to refresh data
                    setReload(!reload)
                  }}
                  maxTags={10}
                />
              </Stack>

              {/* Action buttons container */}
              <Stack
                flexDirection={isMobile ? 'column' : 'row'}
                justifyContent="end"
                gap={1}
              >
                <Can permission="recurrences:cancel">
                  <>
                    {recurrence?.status !== 'cancelled' && (
                      <Grid item={true}>
                        <Button
                          onClick={() => setIsOpenCancellationDialog(true)}
                          variantButton="neutral"
                          fullWidth={isMobile}
                        >
                          Cancelar Recorrência
                        </Button>
                      </Grid>
                    )}
                  </>
                </Can>
                <Can permission="recurrences:manage">
                  <Button
                    onClick={handleOpenEditCharge}
                    variantButton={
                      recurrence?.status === 'cancelled' ? 'neutral' : 'primary'
                    }
                    fullWidth={isMobile}
                  >
                    Editar recorrência
                  </Button>
                </Can>
                {recurrence?.status === 'cancelled' && (
                  <Can permission="recurrences:resume">
                    <Button
                      onClick={() => setIsOpenReactivateModal(true)}
                      fullWidth={isMobile}
                    >
                      Reativar Recorrência
                    </Button>
                  </Can>
                )}
              </Stack>
            </Stack>
          </ModalFooter>
        )}

        <ModalChargeDetail
          handleCloseChargeDetail={handleCloseDetailInvoice}
          isOpenChargeDetail={modalDialog.isOpenDetailInvoice}
          uuid={modalDialog.uuid}
          disablePortal={false}
        />

        <ModalInstallmentDetail
          handleCloseInstallmentDetail={handleCloseInstallmentDetail}
          isOpenInstallmentDetail={modalDialog.isOpenInstallmentDetail}
          uuid={modalDialog.uuid}
        />

        <ModalEditRecurrenceUnified
          onClose={handleCloseEditCharge}
          uuid={uuid}
          recurrence={recurrence}
          refetch={() => {
            setReload(!reload)
            !!reloadTable && reloadTable((s) => !s)
          }}
          nameCustomer={recurrence.customer?.name || ''}
          open={isOpenModalEdit}
        />

        {isOpenDuplicate && (
          <ModalNewRecurrence
            isOpenModalNewRecurrence={isOpenDuplicate}
            handleCloseModal={() => setIsOpenDuplicate(false)}
            onClose={() => setIsOpenDuplicate(false)}
            defaultValues={duplicateDefaults}
            isDuplicate
            handleOpenDetailChargeOrRecurrence={({ uuid: newUuid }) => {
              setIsOpenDuplicate(false)
              if (onOpenDetailForUuid) {
                onOpenDetailForUuid(newUuid)
              }
            }}
            revalidateTable={() => reloadTable?.((s) => !s)}
          />
        )}
      </ModalBody>

      <CancelConfirmationModal
        cancellationData={recurrence.cancellationReason}
        isOpen={isOpenCancellationDialog}
        onClose={handleCloseCancellationDialog}
        billingUuid={recurrence.uuid}
        onSuccess={() => {
          setReload(!reload)
          !!reloadTable && reloadTable((s) => !s)
        }}
        type="recurrence"
      />

      <ModalReactivateRecurrence
        open={isOpenReactivateModal}
        onClose={() => setIsOpenReactivateModal(false)}
        recurrence={recurrence}
        onEdit={() => {
          setIsOpenReactivateModal(false)
          handleOpenEditCharge()
        }}
        onSuccess={() => {
          setReload(!reload)
          !!reloadTable && reloadTable((s) => !s)
        }}
      />

      <CreditCardDetailsDialog
        isOpen={isOpenCreditCardDialog}
        onClose={() => setIsOpenCreditCardDialog(false)}
        recurrenceCard={
          recurrence?.recurrenceCards?.length > 0
            ? recurrence.recurrenceCards[0]
            : null
        }
        recurrenceUuid={recurrence.uuid}
        onSuccess={() => {
          setReload(!reload)
          !!reloadTable && reloadTable((s) => !s)
        }}
      />

      <ApplyAdjustmentDialog
        isOpen={isOpenApplyAdjustmentDialog}
        handleClose={handleCloseApplyAdjustmentDialog}
        recurrenceUuid={recurrence?.uuid || ''}
        existingAdjustment={recurrence?.adjustmentScheduled}
        onSuccess={handleApplyAdjustmentSuccess}
      />

      <Popover
        anchorEl={menuAnchorEl}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom'
        }}
        onClose={handleCloseMenu}
        open={Boolean(menuAnchorEl)}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 200 }}>
          <MenuItem
            onClick={() => {
              handleCloseMenu()
              handleDuplicate()
            }}
            sx={{
              display: 'flex',
              fontSize: 14,
              color: '#646464',
              gap: '8px',
              py: '6px',
              borderRadius: '6px'
            }}
          >
            <Icon icon="contentCopy" colorSX="#000" />
            Duplicar
          </MenuItem>
          {recurrence?.billings?.some((b) => b.contaAzulInfo) && (
            <MenuItem
              onClick={() => {
                handleCloseMenu()
                setIsOpenContaAzul(true)
              }}
              sx={{
                display: 'flex',
                fontSize: 14,
                color: '#646464',
                gap: '8px',
                py: '6px',
                borderRadius: '6px'
              }}
            >
              <Icon icon="link" colorSX="#000" />
              Conta Azul
            </MenuItem>
          )}
          {recurrence?.status === 'active' && (
            <MenuItem
              onClick={() => {
                handleCloseMenu()
                setIsOpenApplyAdjustmentDialog(true)
              }}
              sx={{
                display: 'flex',
                fontSize: 14,
                color: '#646464',
                gap: '8px',
                py: '6px',
                borderRadius: '6px'
              }}
            >
              <Icon icon="eventRepeat" colorSX="#000" />
              Aplicar reajuste
            </MenuItem>
          )}
          {capabilities?.carne?.has_access && (
            <Can permission="carne:export">
              <MenuItem
                onClick={() => {
                  handleCloseMenu()
                  setIsOpenCarneDialog(true)
                }}
                sx={{
                  display: 'flex',
                  fontSize: 14,
                  color: '#646464',
                  gap: '8px',
                  py: '6px',
                  borderRadius: '6px'
                }}
              >
                <Icon icon="receipt" colorSX="#000" />
                Carnê
              </MenuItem>
            </Can>
          )}
        </Box>
      </Popover>

      <CarneDialog
        isOpen={isOpenCarneDialog}
        onClose={() => setIsOpenCarneDialog(false)}
        recurrenceUuid={recurrence?.uuid || ''}
        totalCycles={recurrence?.totalCycles || 0}
        currentCycle={recurrence?.currentCycle || 1}
        billings={recurrence?.billings || []}
      />

      {recurrence?.billings?.some((b) => b.contaAzulInfo) && (
        <ModalContaAzulDetail
          open={isOpenContaAzul}
          onClose={() => setIsOpenContaAzul(false)}
        >
          <ModalContent>
            <ContaAzulRecurrenceTable
              records={contaAzulBillingRows}
              syncingUuid={syncingBillingUuid}
              onSync={handleSyncBilling}
              canManage={canManageContaAzulBillings}
            />
          </ModalContent>
          <ContaAzulResyncFooter
            records={contaAzulBillingRows}
            onResync={handleResyncBillingRecurrence}
            resyncing={resyncingRecurrence}
            canManage={canManageContaAzulBillings}
          />
        </ModalContaAzulDetail>
      )}

      <Dialog
        icon="info"
        title="Remover cupom da recorrência?"
        description="Cobranças futuras serão geradas sem o desconto. Cobranças já pagas manterão o desconto registrado"
        setIsOpenDialog={() => setIsOpenRemoveCouponDialog(false)}
        isOpenDialog={isOpenRemoveCouponDialog}
        cancelButton
        fullWidth
        maxWidth="xs"
      >
        <Button
          fullWidth
          onClick={handleRemoveCoupon}
          disabled={removingCoupon}
          loading={removingCoupon}
        >
          Remover
        </Button>
      </Dialog>
    </Modal>
  )

  function groupBillingsByInstallment(billings: BillingsParams[]) {
    const grouped: { [key: string]: BillingsParams[] } = {}
    const ungrouped: BillingsParams[] = []

    billings.forEach((billing) => {
      if (billing.installment?.uuid) {
        const installmentUuid = billing.installment.uuid
        if (!grouped[installmentUuid]) {
          grouped[installmentUuid] = []
        }
        grouped[installmentUuid].push(billing)
      } else {
        ungrouped.push(billing)
      }
    })

    return { grouped, ungrouped }
  }

  function convertToRows(billings: BillingsParams[]) {
    const { grouped, ungrouped } = groupBillingsByInstallment(billings)
    const rows: any[] = []

    // Adicionar billings não agrupadas (sem installment)
    ungrouped.forEach((billing: BillingsParams) => {
      const dueDate = moment(billing.dueDate, 'YYYY-MM-DD')
        .format('DD/MM/YYYY')
        .toString()

      const paidDate = moment(billing.datePaid, 'YYYY-MM-DD')
        .format('DD/MM/YYYY')
        .toString()

      const isBonified = parseFloat(billing.amountBilled || '0') === 0

      rows.push({
        uuid: billing.uuid,
        sortDate: billing.dueDate,
        dueDate,
        datePaid: billing.datePaid ? paidDate : '',
        amountBilled: isBonified ? (
          <MuiChip
            label="Bonificado"
            size="small"
            sx={{
              fontSize: '10px',
              height: '20px',
              background: theme.palette.primary[500],
              color: theme.palette.primary[200]
            }}
          />
        ) : (
          formatMoney(billing.amountBilled)
        ),
        amountPaid: formatMoney(billing.amountPaid),
        installment: '', // Vazio para billings sem installment
        status: (
          <Box
            sx={{
              ...cycleStatusChipSx,
              ...getStatusStyleCharge(billing.status || '', billing.dueDate)
            }}
          >
            {getStatusOptionCharge(billing.status || '', billing.dueDate).label}
          </Box>
        ),
        action: (
          <Can permissions={['billings:show', 'customers:view_financial']}>
            <Tooltip
              enterTouchDelay={0}
              arrow={true}
              placement="top"
              title="Detalhes cobrança"
            >
              <Box>
                <ButtonRounded
                  onClick={() => handleOpenDetailInvoice(billing.uuid || '')}
                  variant="transparent"
                >
                  <Icon
                    icon="settings"
                    sx={{ color: theme.palette.neutral[200] }}
                  />
                </ButtonRounded>
              </Box>
            </Tooltip>
          </Can>
        )
      })
    })

    // Adicionar billings agrupadas por installment
    Object.entries(grouped).forEach(
      ([installmentUuid, installmentBillings]) => {
        // Usar a billing de vencimento mais antigo como referência do grupo
        const firstBilling = [...installmentBillings].sort(
          (a, b) =>
            moment(a.dueDate, 'YYYY-MM-DD').valueOf() -
            moment(b.dueDate, 'YYYY-MM-DD').valueOf()
        )[0]
        const installment = firstBilling.installment

        if (!installment) return

        // Calcular valores totais do installment
        installmentBillings.reduce(
          (sum, billing) => sum + parseFloat(billing.amountBilled || '0'),
          0
        )
        const totalAmountPaid = installmentBillings.reduce(
          (sum, billing) => sum + parseFloat(billing.amountPaid || '0'),
          0
        )

        // Usar a primeira data de vencimento como referência
        const dueDate = moment(firstBilling.dueDate, 'YYYY-MM-DD')
          .format('DD/MM/YYYY')
          .toString()

        // Verificar se todas as parcelas foram pagas para determinar data de pagamento
        const allPaid = installmentBillings.every(
          (billing) => billing.status === 'paid'
        )
        const lastPaidDate = installmentBillings
          .filter((billing) => billing.datePaid)
          .sort(
            (a, b) =>
              moment(b.datePaid).valueOf() - moment(a.datePaid).valueOf()
          )[0]?.datePaid

        const paidDate =
          allPaid && lastPaidDate
            ? moment(lastPaidDate, 'YYYY-MM-DD').format('DD/MM/YYYY').toString()
            : ''

        // Determinar status do installment (se todas pagas = pago, senão usar status da primeira)
        const installmentStatus = allPaid ? 'paid' : firstBilling.status

        // Calcular valor individual da parcela
        const installmentValue =
          parseFloat(installment.amount) / installment.total_cycles

        rows.push({
          uuid: installmentUuid, // Usar UUID do installment para abrir modal correto
          sortDate: firstBilling.dueDate,
          dueDate,
          datePaid: paidDate,
          amountBilled: formatMoney(installment.amount), // Valor total do installment
          amountPaid: formatMoney(totalAmountPaid.toString()),
          installment: `${installment.total_cycles}x de ${formatMoney(
            installmentValue.toString()
          )}`,
          status: (
            <Box
              sx={{
                ...cycleStatusChipSx,
                ...getStatusStyleCharge(
                  installmentStatus || '',
                  firstBilling.dueDate
                )
              }}
            >
              {
                getStatusOptionCharge(
                  installmentStatus || '',
                  firstBilling.dueDate
                ).label
              }
            </Box>
          ),
          action: (
            <Can
              permissions={['installments:show', 'customers:view_financial']}
            >
              <Tooltip
                enterTouchDelay={0}
                arrow={true}
                placement="top"
                title="Detalhes do parcelamento"
              >
                <Box>
                  <ButtonRounded
                    onClick={() => handleOpenInstallmentDetail(installmentUuid)}
                    variant="transparent"
                  >
                    <Icon
                      icon="open"
                      sx={{ color: theme.palette.neutral[200] }}
                    />
                  </ButtonRounded>
                </Box>
              </Tooltip>
            </Can>
          )
        })
      }
    )

    // Ordena sempre por vencimento — a ordem que vem da API não é cronológica
    // e, sem sort, ciclos atrasados acabavam caindo depois dos futuros
    rows.sort(
      (a, b) =>
        moment(a.sortDate, 'YYYY-MM-DD').valueOf() -
        moment(b.sortDate, 'YYYY-MM-DD').valueOf()
    )

    // sortDate é só para ordenação — a Table renderiza as chaves extras do row
    rows.forEach((row) => delete row.sortDate)
    // Sem parcelamento a coluna sai do header, e a chave precisa sair da linha
    // também: a Table monta as células pelas CHAVES, e uma `installment` vazia
    // sobrando empurraria Status e ações uma coluna para a direita.
    if (Object.keys(grouped).length === 0) {
      rows.forEach((row) => delete row.installment)
    }

    return rows
  }

  /**
   * Card de um ciclo destacado acima da tabela. `highlighted` marca o ciclo
   * atual, que ganha a borda colorida.
   */
  function renderCycleCard(
    label: string,
    cycle: CycleBilling,
    highlighted: boolean
  ) {
    const isBonified = parseFloat(cycle.amountBilled || '0') === 0

    return (
      <Box
        flex={1}
        minWidth={0}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        p={1.5}
        sx={{
          border: '1px solid #E8EBE8',
          borderLeft: '3px solid',
          borderLeftColor: highlighted ? theme.palette.primary.main : '#E8EBE8',
          borderRadius: '8px',
          backgroundColor: highlighted ? '#FAFBFA' : 'transparent'
        }}
      >
        <Box display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="smLight" color="#777777">
            {label}
          </Typography>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="smBold">
              {moment(cycle.dueDate, 'YYYY-MM-DD').format('DD/MM/YYYY')}
            </Typography>
            <Typography variant="smLight" color="#777777">
              •
            </Typography>
            <Typography variant="smBold">
              {isBonified ? 'Bonificado' : formatMoney(cycle.amountBilled)}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              ...cycleStatusChipSx,
              // Fora da tabela o chip herda a fonte do modal e fica maior que
              // os da tabela, que usam o tamanho padrão das células (14px)
              fontSize: `${FONT_SIZE_PX.md}px`,
              ...getStatusStyleCharge(cycle.status || '', cycle.dueDate)
            }}
          >
            {getStatusOptionCharge(cycle.status || '', cycle.dueDate).label}
          </Box>
          <Can permissions={['billings:show', 'customers:view_financial']}>
            <Tooltip
              enterTouchDelay={0}
              arrow={true}
              placement="top"
              title="Detalhes cobrança"
            >
              <Box>
                <ButtonRounded
                  onClick={() => handleOpenDetailInvoice(cycle.uuid || '')}
                  variant="transparent"
                >
                  <Icon
                    icon="settings"
                    sx={{ color: theme.palette.neutral[200] }}
                  />
                </ButtonRounded>
              </Box>
            </Tooltip>
          </Can>
        </Box>
      </Box>
    )
  }

  /**
   * Destaque fixo do ciclo atual (`current_cycle`) e do anterior
   * (`current_cycle - 1`) acima da tabela, para não depender do scroll.
   */
  function renderCurrentCycleHighlight() {
    const billings = recurrence?.billings || []
    const { previous, current } = resolveHighlightedCycles(
      billings,
      recurrence?.currentCycle
    )

    if (!current) return null

    return (
      <Box
        display="flex"
        gap={1.5}
        mb={1.5}
        flexDirection={{ xs: 'column', sm: 'row' }}
      >
        {previous && renderCycleCard('Ciclo anterior', previous, false)}
        {renderCycleCard('Ciclo atual', current, true)}
      </Box>
    )
  }

  function cardTittle(label: string, icon: Icons, iconColor?: string) {
    return (
      <Box display="flex" alignItems={'center'}>
        <Box display={'flex'} alignItems={'center'} gap={'8px'}>
          <S.ContainerIcon>
            {' '}
            <Icon icon={icon} colorSX={iconColor} />
          </S.ContainerIcon>
          <Typography variant="smLight">{label}</Typography>
        </Box>
      </Box>
    )
  }
}

export default ModalRecurrenceDetail
